import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Competition from '../components/pageComponents/Home/Competition.jsx';
import BackButton from '../components/reusable/buttons/BackButton';
import ReactionListCard from '../components/reusable/ReactionListCard.jsx';
import Spinner from '../components/reusable/spinner/Spinner';
import UserListCard from '../components/reusable/UserListCard.jsx';
import { ThreeDotsIcon } from '../components/svgIcons';
import Send from '../components/svgIcons/Send';
import { dispatch } from '../redux/store';
import CommentApi from '../services/comment';
import { LiveLikeReactionPacks, TRACKING_EVENTS } from '../utils/constants';
import Analytics from '../utils/google-analytics.js';
import {
    isAuthenticated,
    momentGetTimeAgo,
    notifyError
} from '../utils/helpers';
import RequireAuthClick from '../components/reusable/RequireAuthClick';

const AnnouncementPage = () => {
    const [fetchedComments, setFetchedComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [sendLoading, setSendLoading] = useState(false);
    const [comment, setComment] = useState('');
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [currentAnnouncement, setAnnouncement] = useState(null);

    //states for mentions or usertag
    const [isMentioning, setIsMentioning] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');

    const [reportModal, setReportModal] = useState(false);
    const [reportText, setReportText] = useState('');
    const [currentCommentItem, setCurrentCommentItem] = useState(null);

    // Detect "@" and trigger modal
    const handleInputChange = useCallback(e => {
        const { value } = e.target;
        setComment(value);

        // Check for "@" followed by a character (e.g., "@f")
        const mentionStart = value.lastIndexOf('@');
        if (mentionStart !== -1 && value.length > mentionStart + 1) {
            setIsMentioning(true);
            setMentionQuery(value.slice(mentionStart + 1)); // Query after "@"
        } else {
            // Close the modal if the mention criteria is not met
            setIsMentioning(false);
        }
    }, []);

    // Handle adding the selected mention to the input
    const handleMentionSelect = mention => {
        const mentionStart = comment.lastIndexOf('@');
        const newCommentText = `${comment.slice(0, mentionStart + 1)}${
            mention.firstName
        }${mention.lastName} `;

        setComment(newCommentText);
        setIsMentioning(false);

        // Focus back to input after selecting a mention
        setTimeout(() => {
            commentInputRef.current.focus();
        }, 0);
    };

    const handleCloseModal = () => {
        setReportModal(false);
        setCurrentCommentItem(null);
    };

    const reporting = useSelector(
        ({ loading }) => loading.effects.feed.reportHighlights
    );

    const [shouldShowEmojiPanel, setShouldShowEmojiPanel] = useState({
        visible: false,
        highlightId: ''
    });

    const queryParams = new URLSearchParams(window.location.search);

    const type = queryParams.get('type');
    const [selected, setSelected] = useState(type);

    const announcementId = queryParams.get('announcementId');
    const navigatedFrom = queryParams.get('from') || '';
    const userId = queryParams.get('userId');

    const navigate = useNavigate();
    const emojiPanelRef = useRef(null);

    const commentInputRef = useRef(null);

    const { userInfo } = useSelector(state => state.auth.authUser);

    // Trigger the loading of all announcements
    useEffect(() => {
        dispatch.announcement.getAllAnnouncements();
    }, []);

    const { allAnnouncements, activeAnnouncements } = useSelector(
        ({ announcement }) => ({
            allAnnouncements: announcement.announcements,
            activeAnnouncements: announcement.activeAnnouncements
        })
    );

    const gettingUsers = useSelector(
        ({ loading }) => loading.effects.user?.userSearch
    );

    // Fetch user list from redux state
    const { userList } = useSelector(({ user }) => ({
        userList: user?.usersByProjection || []
    }));

    const fetchAllUserData = async () => {
        await dispatch.user.userSearchByProjection();
    };

    const { loggedUserId } = useSelector(({ auth: { authUser }, user }) => ({
        loggedUserId: authUser?.userInfo?.id || '',
        authUser
    }));

    const togglePanel = () => {
        setIsPanelOpen(!isPanelOpen);
    };

    const handleButtonClick = e => {
        e.stopPropagation();
        togglePanel();
    };

    const onReactionClick = type => {
        setSelected(type);
    };

    const handleClickOutside = event => {
        if (
            emojiPanelRef.current &&
            !emojiPanelRef.current.contains(event.target)
        ) {
            setIsPanelOpen(false);
        }
    };

    // ==========================================================================//
    // HANDLES SCROLL VIEW AND SET LAST ITEM TO FETCH NEXT COMMENTS
    const ref = useRef();
    const { ref: inViewRef } = useInView({
        onChange: inView => {
            if (inView && nextCursor && !commentsLoading) {
                fetchComments(nextCursor);
            }
        }
    });

    // Use `useCallback` so we don't recreate the function on each render
    const setRefs = useCallback(
        node => {
            // Ref's from useRef needs to have the node assigned to `current`
            ref.current = node;
            // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
            inViewRef(node);
        },
        [inViewRef]
    );
    // ==========================================================================//

    const getCurrentAnnoucement = () => {
        setAnnouncement(
            allAnnouncements.filter(item => item.id === announcementId)[0]
        );
    };

    const fetchComments = useCallback(
        async cursor => {
            if (!currentAnnouncement?.commentBoardId) return;

            try {
                setCommentsLoading(true);
                const { data } = await CommentApi.getComments(
                    currentAnnouncement.commentBoardId,
                    cursor
                );

                if (cursor) {
                    setFetchedComments(prev =>
                        [...prev, ...data.comments].sort(
                            (a, b) =>
                                new Date(b.createdAt) - new Date(a.createdAt)
                        )
                    );
                } else {
                    setFetchedComments(data.comments);
                }

                setNextCursor(data.pagination.nextCursor || null);
            } catch (error) {
                console.error('Error fetching comments:', error);
                notifyError('Error fetching comments.');
            } finally {
                setCommentsLoading(false);
            }
        },
        [currentAnnouncement?.commentBoardId]
    );

    const openReportModal = item => {
        setCurrentCommentItem(item);
        setReportModal(true);
    };

    const handleReportSubmit = async () => {
        if (!currentCommentItem) return;

        const reporterFullName = `${userInfo?.firstName}${
            userInfo?.lastName ? ` ${userInfo?.lastName}` : ''
        }`;
        const reportedFullName = `${currentCommentItem.user.firstName}${
            currentCommentItem.user.lastName
                ? ` ${currentCommentItem.user.lastName}`
                : ''
        }`;

        const data = {
            reporterUserId: userInfo?.id,
            reportedUserId: currentCommentItem.user.id,
            reporterFullName,
            reportedFullName,
            reason: reportText,
            reportedContent: currentCommentItem.text,
            reportedPhotoUrl: '',
            reporterPhotoUrl: userInfo?.photoUrl || '',
            reportType: 'COMMENT',
            contendId: currentCommentItem.id,
            metadata: {
                announcementId,
                commentBoardId: highlight.commentBoardId,
                userId
            }
        };

        if (reportText === '') {
            notifyError('A reason is required');
        } else {
            const res = await dispatch.feed.reportHighlights(data);

            if (res.status === 1) {
                handleCloseModal(true);
                setReportText('');
                setCurrentCommentItem(null);
            } else {
                notifyError('Something went wrong');
            }
        }
    };

    const addUserComment = async () => {
        try {
            setSendLoading(true);

            const { data: createdComment } = await CommentApi.createComment(
                currentAnnouncement.commentBoardId,
                { comment }
            );

            setFetchedComments(prev => [createdComment, ...prev]);

            Analytics.trackEvent({
                name: TRACKING_EVENTS.COMMENT_ADDED,
                metadata: {
                    content_id: currentAnnouncement.id,
                    content_type: 'announcement',
                    comment_length: comment.length
                }
            });

            commentInputRef.current.value = '';
            setComment('');
        } catch (error) {
            console.error('Error creating comment:', error);
            notifyError('Error creating comment. Please try again.');
        } finally {
            setSendLoading(false);
        }
    };

    const handleSearch = async data => {
        const splitName = data.split(' ');
        const filteredNames = splitName.filter(name => name !== '');
        const checkThirdName = filteredNames[2]
            ? !filteredNames[2].includes('(')
            : false;
        const fullName = `${filteredNames[0]} ${filteredNames[1]}${
            checkThirdName ? ` ${filteredNames[2]}` : ''
        }`;

        const searchResult = await handleSearch(fullName);
        if (searchResult.length > 0) {
            handleOpenProfile(searchResult[0].id);
        } else {
            notifyError('User profile not found');
        }
    };

    const handleOpenProfile = id => {
        if (id) {
            navigate({ pathname: '/profile', search: `?id=${id}` });
        } else {
            navigate('/login');
        }
    };

    useEffect(() => {
        getCurrentAnnoucement();
    }, [type]);

    useEffect(() => {
        console.log('currentAnnouncement', currentAnnouncement);
        if (currentAnnouncement) {
            fetchComments();
        }
    }, [currentAnnouncement]);

    useEffect(() => {
        if (!userList?.length && isAuthenticated()) {
            fetchAllUserData();
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const Avatar = ({ name, id }) => {
        // Get the first letter of the name or use a default placeholder
        const initial = name ? name.charAt(0).toUpperCase() : '?';

        return (
            <RequireAuthClick popupType='profile'>
                <div
                    onClick={() => handleOpenProfile(id)}
                    className='bg-gray-300 text-white rounded-full flex items-center justify-center'
                    style={{ width: '45px', height: '45px' }}
                >
                    <span className='text-lg font-bold text-[#000000]'>
                        {initial}
                    </span>
                </div>
            </RequireAuthClick>
        );
    };

    return (
        <div className='pb-[70px] px-0'>
            <div className='flex justify-between w-full h-[7vh] px-2'>
                <BackButton from={`/${navigatedFrom}`} />
            </div>

            <div>
                <Competition
                    activeAnnouncements={[currentAnnouncement]}
                    isDetailPage={true}
                    loggedUserId={loggedUserId}
                    reactionPacks={LiveLikeReactionPacks}
                    onReactionClick={onReactionClick}
                    setCurrentAnnouncement={setAnnouncement}
                />

                {currentAnnouncement && (
                    <div key={currentAnnouncement?.id}>
                        {currentAnnouncement?.announcementType === 'VOTE' && (
                            <div>
                                {currentAnnouncement?.voteSubmittedAssets.map(
                                    (item, index) => (
                                        <div key={index} className='px-4 pb-2'>
                                            <div className='w-full'>
                                                <p
                                                    className='text-[#7582B2] text-[12px] font-[400] underline inline'
                                                    onClick={() =>
                                                        handleOpenProfile(
                                                            item?.userId
                                                        )
                                                    }
                                                >
                                                    {item?.orderIndex}.{' '}
                                                    {item?.userName}
                                                </p>
                                                <span className='px-2'>
                                                    {' '}
                                                    -{' '}
                                                </span>
                                                <p className='text-[#000000] text-[12px] font-[400] text-left inline'>
                                                    {item?.text}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {currentAnnouncement?.announcementType === 'WINNER' && (
                            <div>
                                {/* <p className="px-4">Special mentions to the SOTM runner ups:</p> */}
                                {currentAnnouncement?.voteSubmittedAssets.map(
                                    (item, index) => (
                                        <div
                                            key={index}
                                            className='px-4 py-[1px]'
                                        >
                                            <div className='grid grid-cols-[45%_5%_50%] w-full'>
                                                <p
                                                    className='text-[#7582B2] text-[12px] font-[400] underline cursor-pointer'
                                                    onClick={() =>
                                                        handleOpenProfile(
                                                            item?.userId
                                                        )
                                                    }
                                                >
                                                    {item?.userName}
                                                </p>
                                                <span className=''>-</span>
                                                <p className='text-[#000000] text-[12px] font-[400] text-left'>
                                                    {item?.text}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {/* <div className="w-full h-[2px] bg-[#A5A5A5] mt-10" /> */}

                        {selected === 'reactions' ? (
                            <div className='flex flex-col mt-5 mb-10 mx-4'>
                                {currentAnnouncement?.reactedByUsers?.length >
                                    0 &&
                                    currentAnnouncement.reactedByUsers.map(
                                        (user, idx) => (
                                            <div key={idx}>
                                                <ReactionListCard
                                                    reaction={user.others}
                                                    userId={user.userId}
                                                />
                                            </div>
                                        )
                                    )}
                            </div>
                        ) : (
                            <div className='flex flex-col mt-5 mb-10 mx-4'>
                                {fetchedComments.length > 0 &&
                                    fetchedComments.map((comment, idx) => {
                                        return (
                                            <div
                                                key={comment.id}
                                                ref={
                                                    fetchedComments.length -
                                                        1 ===
                                                    idx
                                                        ? setRefs
                                                        : null
                                                }
                                                className={`${
                                                    idx !==
                                                    fetchedComments.length - 1
                                                        ? 'border-b'
                                                        : ''
                                                } border-gray-300 pb-2`}
                                            >
                                                <div className='flex justify-between items-start mt-5'>
                                                    <div className='flex space-x-5 '>
                                                        {comment.user
                                                            .photoUrl ? (
                                                            <RequireAuthClick popupType='profile'>
                                                                <img
                                                                    src={
                                                                        comment
                                                                            .user
                                                                            .photoUrl ||
                                                                        '/images/profile.png'
                                                                    }
                                                                    alt=''
                                                                    className='w-[45px] h-[45px] rounded-full border border-1 border-[#000000]'
                                                                    onClick={() => {
                                                                        handleOpenProfile(
                                                                            comment
                                                                                .user
                                                                                .id
                                                                        );
                                                                    }}
                                                                />
                                                            </RequireAuthClick>
                                                        ) : (
                                                            <Avatar
                                                                id={
                                                                    comment.user
                                                                        .id
                                                                }
                                                                name={
                                                                    comment.user
                                                                        .firstName
                                                                }
                                                            />
                                                        )}
                                                        <div>
                                                            <div className='flex space-x-6'>
                                                                <RequireAuthClick popupType='profile'>
                                                                    <p
                                                                        className='text-[#000000] text-[12px] font-[600]'
                                                                        onClick={() =>
                                                                            handleOpenProfile(
                                                                                comment
                                                                                    .user
                                                                                    .id
                                                                            )
                                                                        }
                                                                    >
                                                                        {
                                                                            comment
                                                                                .user
                                                                                .firstName
                                                                        }
                                                                    </p>
                                                                </RequireAuthClick>
                                                                <p className='text-[#808080] text-[12px] font-[600]'>
                                                                    {momentGetTimeAgo(
                                                                        comment
                                                                            .user
                                                                            .createdAt
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <p className='text-[12px] text-[#000000] mt-1'>
                                                                {comment.text}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {loggedUserId && (
                                                        <div className='flex items-center'>
                                                            <div className='dropdown dropdown-left dropdown-bottom'>
                                                                <label
                                                                    tabIndex={1}
                                                                    className='flex items-center'
                                                                >
                                                                    <ThreeDotsIcon
                                                                        color={
                                                                            '#A5A5A5'
                                                                        }
                                                                    />
                                                                </label>
                                                                {userInfo?.id !==
                                                                    comment.user
                                                                        .id && (
                                                                    <div
                                                                        tabIndex={
                                                                            1
                                                                        }
                                                                        className='dropdown-content card card-compact w-56 shadow-md bg-white text-primary-content'
                                                                    >
                                                                        <div className='p-4 text-black '>
                                                                            <div className='mt-[10px] flex flex-col gap-5 w-full'>
                                                                                <div
                                                                                    onClick={() => {
                                                                                        openReportModal(
                                                                                            comment
                                                                                        );
                                                                                    }}
                                                                                    className='text-red-600 flex gap-3 items-center'
                                                                                >
                                                                                    Report
                                                                                    comment
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {/* Modal for reporting comment */}
                                                                {reportModal && (
                                                                    <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center'>
                                                                        <div className='bg-white p-6 rounded shadow-lg w-[400px]'>
                                                                            <h2 className='text-lg font-bold mb-4'>
                                                                                Reason
                                                                                for
                                                                                reporting
                                                                                this
                                                                                comment
                                                                            </h2>
                                                                            <textarea
                                                                                className='w-full h-24 p-2 border border-gray-300 rounded mb-4 bg-[#ffffff]'
                                                                                placeholder='Enter your reason for reporting'
                                                                                value={
                                                                                    reportText
                                                                                }
                                                                                onChange={e =>
                                                                                    setReportText(
                                                                                        e
                                                                                            .target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div className='flex justify-end gap-3'>
                                                                                <button
                                                                                    className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500'
                                                                                    onClick={() =>
                                                                                        handleReportSubmit()
                                                                                    }
                                                                                >
                                                                                    {reporting
                                                                                        ? 'Submiting'
                                                                                        : 'Submit'}
                                                                                </button>
                                                                                <button
                                                                                    className='px-4 py-2 bg-gray-300 rounded hover:bg-gray-400'
                                                                                    onClick={
                                                                                        handleCloseModal
                                                                                    }
                                                                                >
                                                                                    Close
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                {commentsLoading && (
                                    <div className='flex w-full justify-center'>
                                        <Spinner />
                                    </div>
                                )}
                            </div>
                        )}
                        {loggedUserId && (
                            <div className='fixed bottom-0 bg-white backdrop-blur-3xl py-2 px-[15px] w-full -ml-2'>
                                <div className='relative w-full flex items-center justify-between'>
                                    <textarea
                                        type='text'
                                        name='comment'
                                        ref={commentInputRef}
                                        value={comment}
                                        onChange={handleInputChange} // Handle the input change for "@" detection
                                        id='comment'
                                        className='border-b-2 border-gray-600 block pt-3 pb-1 px-0 pr-[60px] w-full text-gray-900 bg-transparent appearance-none focus:outline-none focus:ring-0 focus:border-gray-600 peer'
                                        placeholder='Add a comment'
                                        // maxLength={500}
                                    />
                                    <button
                                        disabled={comment.trim().length === 0}
                                        onClick={() => {
                                            if (
                                                currentAnnouncement &&
                                                userInfo?.id
                                            ) {
                                                addUserComment();
                                            }
                                        }}
                                        className={`absolute w-[43.88px] h-[44.5px] mb-6 flex justify-center items-center right-2 ${
                                            sendLoading ? '' : 'bg-[#14FF00]'
                                        } disabled:opacity-50  rounded-xl`}
                                    >
                                        {sendLoading ? <Spinner /> : <Send />}
                                    </button>
                                </div>

                                {/* Render the modal for mentions */}
                                {isMentioning && (
                                    <UserListCard
                                        query={mentionQuery}
                                        onSelect={handleMentionSelect}
                                        closeModal={() =>
                                            setIsMentioning(false)
                                        }
                                        isMentioning={isMentioning}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
export default AnnouncementPage;
